import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Loader2, Wrench, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, EVENTS } from '../../lib/analytics';

const MobileLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enablePin, setEnablePin] = useState(true);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login'); // 'login' or 'pin-setup'
  
  const { login, setupPin } = useMobileAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/mobile';

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await mobileApi.login({
        email,
        password
      });

      if (response.data.token) {
        trackEvent(EVENTS.MOBILE_LOGIN, { method: 'password', pin_enabled: !!enablePin });
        if (enablePin) {
          // Store temp data and go to PIN setup
          sessionStorage.setItem('temp_login_data', JSON.stringify(response.data));
          setStep('pin-setup');
        } else {
          // Login without PIN
          login(response.data.user, response.data.token);
          navigate(from, { replace: true });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSetup = async (e) => {
    e.preventDefault();

    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      toast.error('PIN must be 4 digits');
      return;
    }

    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    setLoading(true);
    try {
      const tempData = JSON.parse(sessionStorage.getItem('temp_login_data'));
      
      // Set up PIN on server
      await mobileApi.setupPin(pin, tempData.token);
      
      // Complete login
      setupPin(pin);
      login(tempData.user, tempData.token);
      sessionStorage.removeItem('temp_login_data');
      
      toast.success('PIN set up successfully');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPin = () => {
    const tempData = JSON.parse(sessionStorage.getItem('temp_login_data'));
    login(tempData.user, tempData.token);
    sessionStorage.removeItem('temp_login_data');
    navigate(from, { replace: true });
  };

  if (step === 'pin-setup') {
    return (
      <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-[var(--volt-tint)] rounded-2xl flex items-center justify-center mb-4">
              <Smartphone className="w-8 h-8 text-[var(--volt-icon)]" />
            </div>
            <h1 className="text-2xl font-bold text-[var(--ink-primary)]">Set Up Quick PIN</h1>
            <p className="text-[var(--ink-tertiary)] mt-2">
              Create a 4-digit PIN for faster access next time
            </p>
          </div>

          <form onSubmit={handlePinSetup} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[var(--ink-secondary)]">Enter PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="****"
                className="text-center text-2xl tracking-[0.5em] bg-[var(--surface-card)] border-[var(--border)] text-[var(--ink-primary)] h-14"
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[var(--ink-secondary)]">Confirm PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                placeholder="****"
                className="text-center text-2xl tracking-[0.5em] bg-[var(--surface-card)] border-[var(--border)] text-[var(--ink-primary)] h-14"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-[var(--volt)] hover:bg-[var(--volt)] text-black font-semibold"
              disabled={loading || pin.length !== 4 || confirmPin.length !== 4}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set PIN & Continue'}
            </Button>

            <button
              type="button"
              onClick={handleSkipPin}
              className="w-full text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)] text-sm"
            >
              Skip for now
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[var(--volt-tint)] rounded-2xl flex items-center justify-center mb-4">
            <Wrench className="w-8 h-8 text-[var(--volt-icon)]" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--ink-primary)]">Guardian Mobile</h1>
          <p className="text-[var(--ink-tertiary)] mt-2">Sign in to manage your work</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[var(--ink-secondary)]">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="bg-[var(--surface-card)] border-[var(--border)] text-[var(--ink-primary)] h-12"
              autoCapitalize="none"
              autoComplete="email"
              inputMode="email"
              data-testid="mobile-login-email"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[var(--ink-secondary)]">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-[var(--volt-icon)] hover:text-[var(--volt-icon)]"
                data-testid="mobile-login-forgot"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              className="bg-[var(--surface-card)] border-[var(--border)] text-[var(--ink-primary)] h-12"
              autoComplete="current-password"
              data-testid="mobile-login-password"
            />
          </div>

          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="enable-pin"
              checked={enablePin}
              onCheckedChange={setEnablePin}
              className="border-[var(--border-strong)] data-[state=checked]:bg-[var(--volt)] data-[state=checked]:border-[var(--volt-edge)]"
            />
            <label
              htmlFor="enable-pin"
              className="text-sm text-[var(--ink-secondary)] cursor-pointer"
            >
              Enable Quick PIN for faster login
            </label>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-[var(--volt)] hover:bg-[var(--volt)] text-black font-semibold"
            disabled={loading}
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
          </Button>
        </form>

        <p className="text-center text-[var(--ink-tertiary)] text-xs mt-6">
          Guardian Mobile v1.0
        </p>
      </div>
    </div>
  );
};

export default MobileLogin;
